/**
 * PRICEUNIFY-1 · 售价总览 › 按渠道 Tab 的价格矩阵（样稿 designs/mockups/pricing/售价入口统一-样稿.html ①②）。
 * 同行 / 散客·手机站 / 社媒 / API 平台 四个 Tab；固定列 成本/人（成本查看权限）+ 1–4 人/间（一日游合并成「每人」一个价，门票成人 / 儿童）；
 * 多选项产品默认收起、点 ▾ 展开，只有 1 个选项的直接一行；同行 Tab 显示结算价，鼠标放上去看建议售价；「改价 →」跳到该选项「售价」页（唯一录价入口）；
 * 「导出 Excel」：按选项一行，同行可勾「同时导出建议售价」，有成本权限可勾「含成本」。
 *   GET /api/v1/pricing/price-overview?channel=   ·  GET /api/v1/pricing/price-overview/export
 * 🔴 只读展示：取价全部来自成本引擎（和选项「售价」页 / 同行站 / 下单同一个结果），这里不算任何价、不改任何价。
 */
import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Download } from "lucide-react";
import { Card } from "@/shared/ui/Card";
import { LoadingState } from "@/shared/ui/PageHeader";
import { useCan } from "@/auth/permissions";
import { PG, SRC_TAG } from "./priceGridStyle";   // PRICEUNIFY-2 · 与选项卡片 / 选项「售价」页共用同一套表格样式
import { downloadPriceOverviewXlsx, getPriceOverview, type CellSource, type OverviewCell, type OverviewOption, type OverviewProduct, type OverviewTab } from "@/services/pricing";

const usd = (c?: number | null) => (c == null ? "—" : `$${(c / 100).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);
const TABS: { key: OverviewTab; label: string }[] = [
  { key: "b2b", label: "同行" }, { key: "direct", label: "散客 / 手机站" }, { key: "social", label: "社媒" }, { key: "api", label: "API 平台" },
];
const SRC = SRC_TAG;

const costOf = (o: OverviewOption) => (o.cost ? (o.cost["2"] ?? o.cost.all) : null);
const tip = (c: OverviewCell | undefined, tab: OverviewTab) => (tab === "b2b" && c?.list_cents != null ? `建议售价 ${usd(c.list_cents)}` : undefined);
const sourceOf = (o: OverviewOption, multi: boolean): CellSource | null => (multi ? o.cells["2"] : o.cells.all)?.source ?? null;

function PriceCells({ p, o, tab }: { p: OverviewProduct; o: OverviewOption; tab: OverviewTab }) {
  if (!p.multi) {
    const a = o.cells.all, kid = o.cells.child;
    return (
      <td colSpan={4} className="px-3 py-2 tabular-nums" data-testid={`ov-cells-${o.code}`}>
        <span title={tip(a, tab)} data-testid={`ov-all-${o.code}`}>{kid ? "成人 " : ""}<b>{usd(a?.cents)}</b></span>
        {kid && <span className="ml-3" title={tip(kid, tab)} data-testid={`ov-child-${o.code}`}>儿童 <b>{usd(kid.cents)}</b></span>}
        <span className="ml-2 text-[11px] text-slate-400">{kid ? "" : "每人（不分房）"}</span>
      </td>
    );
  }
  return <>{["1", "2", "3", "4"].map((k) => <td key={k} className="px-3 py-2 tabular-nums" title={tip(o.cells[k], tab)} data-testid={`ov-t${k}-${o.code}`}>{usd(o.cells[k]?.cents)}</td>)}</>;
}

export function PriceMatrixCard({ productId }: { productId?: string } = {}) {   // productId = 产品详情「资源与售价」里只看本产品的各选项价格对照（全部展开、无搜索 / 导出）
  const canCost = useCan("pricing.cost.view");
  const [tab, setTab] = useState<OverviewTab>("b2b");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [exportOpen, setExportOpen] = useState(false);
  const [withList, setWithList] = useState(false);
  const [withCost, setWithCost] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ovQ = useQuery({ queryKey: ["pricing", "price-overview", tab, productId ?? null], queryFn: () => getPriceOverview(tab, productId) });
  const single = !!productId;
  const showCost = ovQ.data?.show_cost ?? false;
  const products = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return (ovQ.data?.products ?? []).filter((p) => !kw || p.name_cn.toLowerCase().includes(kw) || p.code.toLowerCase().includes(kw));
  }, [ovQ.data, q]);

  return (
    <Card title={single ? "各选项价格对照" : "按渠道看价格"} action={<span className="text-[11px] text-slate-400">只读 · 要改价点「改价 →」到该选项的「售价」页</span>}>
      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="ov-toolbar">
        <div role="tablist" className="flex gap-1 border-b border-slate-200">
          {TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} data-testid={`ov-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`border-b-2 px-4 py-2 text-[13px] font-medium ${tab === t.key ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{t.label}</button>
          ))}
        </div>
        {!single && <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜索产品名 / 编码..." className="ml-auto h-8 w-56 rounded-md border border-slate-300 px-3 text-[13px] focus:border-blue-500 focus:outline-none" />}
        {!single && <div className="relative">
          <button type="button" data-testid="ov-export" onClick={() => setExportOpen((v) => !v)} className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-[13px] font-semibold text-white hover:bg-blue-700"><Download size={14} />导出 Excel</button>
          {exportOpen && (
            <div className="absolute right-0 z-20 mt-1 w-[280px] rounded-lg border border-slate-200 bg-white p-3 text-[12.5px] shadow-lg" data-testid="ov-export-pop">
              <div className="mb-2 font-medium text-slate-800">导出「{TABS.find((t) => t.key === tab)?.label}」价</div>
              <div className="mb-2 text-slate-500">按选项一行；{tab === "b2b" ? "1–4 人/间结算价" : "1–4 人/间价格"}（一日游是每人价）。</div>
              {tab === "b2b" && <label className="mb-1 flex items-center gap-1.5"><input type="checkbox" data-testid="ov-export-list" checked={withList} onChange={(e) => setWithList(e.target.checked)} />同时导出建议售价</label>}
              {canCost && <label className="mb-1 flex items-center gap-1.5"><input type="checkbox" data-testid="ov-export-cost" checked={withCost} onChange={(e) => setWithCost(e.target.checked)} />含成本<span className="text-slate-400">（发同行前别勾）</span></label>}
              {err && <div className="mb-1 text-rose-600">{err}</div>}
              <div className="mt-2 flex justify-end gap-2">
                <button type="button" onClick={() => setExportOpen(false)} className="h-7 rounded-md border border-slate-200 px-2.5 text-slate-600">取消</button>
                <button type="button" data-testid="ov-export-go" onClick={() => { setErr(null); downloadPriceOverviewXlsx(tab, { includeList: withList && tab === "b2b", includeCost: withCost && canCost }).then(() => setExportOpen(false)).catch((e: Error) => setErr(e.message)); }} className="h-7 rounded-md bg-blue-600 px-2.5 font-semibold text-white">导出</button>
              </div>
            </div>
          )}
        </div>}
      </div>
      <p className="mb-2 text-[12px] text-slate-500" data-testid="ov-note">{tab === "b2b" ? "同行 Tab 显示结算价；鼠标放到价格上看建议售价。" : "各渠道各自的价；API 平台 = 我们到手的净价。"}成本列只有「成本查看」权限才显示。</p>

      {ovQ.isError ? (
        <div className="flex items-center justify-center gap-2 py-8 text-[13px] text-slate-500">加载失败 · <button onClick={() => ovQ.refetch()} className="font-medium text-blue-600 hover:underline">重试</button></div>
      ) : ovQ.isLoading ? <LoadingState /> : (
        <div className={PG.wrap}>
          <table className={`${PG.table} min-w-[860px]`} data-testid="ov-table">
            <thead className={PG.thead}>
              <tr>
                <th className={PG.th}>产品 / 选项</th>
                {showCost && <th className={PG.th}>成本/人</th>}
                {[1, 2, 3, 4].map((n) => <th key={n} className={PG.th}>{n} 人/间</th>)}
                <th className={PG.th}>来源</th>
                <th className={PG.th} />
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                const opts = p.options ?? [];
                if (p.error) return <tr key={p.product_id} className="border-t border-slate-100 text-slate-400"><td className="px-3 py-2" colSpan={showCost ? 8 : 7}>{p.code} {p.name_cn} · {p.error}</td></tr>;
                if (opts.length === 0) return null;
                const first = opts[0];
                const many = opts.length > 1;
                const expanded = single || !!open[p.product_id];
                const src = sourceOf(first, !!p.multi);
                return (
                  <Fragment key={p.product_id}>
                    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ov-row-${p.code}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          {many ? <button type="button" aria-label={expanded ? "收起" : "展开"} data-testid={`ov-toggle-${p.code}`} onClick={() => setOpen((s) => ({ ...s, [p.product_id]: !s[p.product_id] }))} className="text-slate-500">{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</button> : <span className="w-3.5" />}
                          <Link to={`/admin/pricing/product/${p.product_id}`} className="font-medium text-blue-600 hover:underline">{p.name_cn}</Link>
                          <span className="text-[11px] text-slate-400">{p.code}{many ? ` · ${opts.length} 个选项` : ""}{!p.multi && p.category !== "ticket" ? " · 不住宿" : ""}</span>
                        </div>
                      </td>
                      {showCost && <td className="px-3 py-2 tabular-nums text-slate-600">{usd(costOf(first))}</td>}
                      <PriceCells p={p} o={first} tab={tab} />
                      <td className="px-3 py-2">{src && <span className={`rounded-full px-2 py-px text-[11px] ${SRC[src].cls}`}>{SRC[src].label}</span>}</td>
                      <td className="px-3 py-2 text-right">{!many && <Link to={`/products/${p.product_id}/options/${first.option_id}?pane=pricing`} data-testid={`ov-edit-${first.code}`} className="text-blue-600 hover:underline">改价 →</Link>}</td>
                    </tr>
                    {many && expanded && opts.map((o) => {
                      const s = sourceOf(o, !!p.multi);
                      return (
                        <tr key={o.option_id} className="border-t border-slate-100 bg-slate-50/60" data-testid={`ov-opt-${o.code}`}>
                          <td className="px-3 py-2 pl-9 text-slate-700">{o.name_cn} <span className="text-[11px] text-slate-400">{o.code}{o.is_default ? " · 第一个选项" : ""}</span></td>
                          {showCost && <td className="px-3 py-2 tabular-nums text-slate-600">{usd(costOf(o))}</td>}
                          <PriceCells p={p} o={o} tab={tab} />
                          <td className="px-3 py-2">{s && <span className={`rounded-full px-2 py-px text-[11px] ${SRC[s].cls}`}>{SRC[s].label}</span>}</td>
                          <td className="px-3 py-2 text-right"><Link to={`/products/${p.product_id}/options/${o.option_id}?pane=pricing`} data-testid={`ov-edit-${o.code}`} className="text-blue-600 hover:underline">改价 →</Link></td>
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })}
              {products.length === 0 && <tr><td colSpan={showCost ? 8 : 7} className="px-3 py-8 text-center text-slate-400">没有产品</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-2 text-[11px] text-slate-400">多选项产品默认收起，点 ▾ 展开；只有 1 个选项的直接一行。第一个选项的价就是产品价；其它选项「跟第一个」= 价格联动。渠道系数、节假日、客户专属价、改价记录这些全局功能不变。</div>
    </Card>
  );
}
